import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

// Re-export array utils for convenience
export * from './array-utils';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extract the message the API sent for a failed request.
 * Os erros chegam como ApiError (objeto simples, não instância de Error), então
 * `error instanceof Error` deixaria escapar a mensagem devolvida pelo backend.
 * @param error - The rejected value from a service/apiClient call
 * @param fallback - Message to use when the error carries none
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }

  return fallback;
}

/**
 * Format CPF number to display format
 * @param cpf - The CPF number (string with or without formatting)
 * @returns Formatted CPF (000.000.000-00) or empty string if invalid
 */
export function formatCpf(cpf: string): string {
  if (!cpf) return '';
  
  // Remove all non-numeric characters
  const numbers = cpf.replace(/\D/g, '');
  
  // Check if it has 11 digits
  if (numbers.length !== 11) return cpf; // Return original if not 11 digits
  
  // Format as 000.000.000-00
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Format phone number to display format
 * @param phone - The phone number (string with or without formatting)
 * @returns Formatted phone ((00) 00000-0000 or (00) 0000-0000) or empty string if invalid
 */
export function formatPhone(phone: string): string {
  if (!phone) return '';
  
  // Remove all non-numeric characters
  const numbers = phone.replace(/\D/g, '');
  
  // Check if it has 10 or 11 digits
  if (numbers.length === 11) {
    // Mobile phone: (00) 00000-0000
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 10) {
    // Landline: (00) 0000-0000
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  // Return original if not 10 or 11 digits
  return phone;
}
